/**
 * Kubista Muse — Theme JavaScript
 * Handles: header, mobile nav, variant selection, scroll animations,
 *          parallax, gold drip click effect
 */

(function () {
  'use strict';

  /* ── Header: scroll transparency + mobile menu ── */
  class KubistaHeader extends HTMLElement {
    connectedCallback() {
      this._header    = this.querySelector('.site-header');
      this._toggle    = this.querySelector('.nav-toggle');
      this._mobileNav = this.querySelector('.mobile-nav');

      this._toggle?.addEventListener('click', () => this._toggleMenu());
      window.addEventListener('scroll', () => this._onScroll(), { passive: true });
      this._onScroll();

      this._mobileNav?.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', () => this._closeMenu());
      });
    }

    _toggleMenu() {
      const open = this._mobileNav.classList.contains('is-open');
      open ? this._closeMenu() : this._openMenu();
    }

    _openMenu() {
      this._mobileNav.classList.add('is-open');
      this._mobileNav.setAttribute('aria-hidden', 'false');
      this._toggle.classList.add('is-active');
      this._toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    _closeMenu() {
      this._mobileNav.classList.remove('is-open');
      this._mobileNav.setAttribute('aria-hidden', 'true');
      this._toggle.classList.remove('is-active');
      this._toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    _onScroll() {
      const scrolled = window.scrollY > 60;
      this._header.classList.toggle('scrolled', scrolled);
    }
  }

  if ('customElements' in window) {
    customElements.define('kubista-header', KubistaHeader);
  }

  /* ── Product: variant radio → hidden select sync ── */
  document.addEventListener('DOMContentLoaded', () => {
    const form    = document.getElementById('kubista-product-form');
    const select  = document.getElementById('product-variant-select');
    const priceEl = document.getElementById('product-price');

    if (!form || !select) return;

    form.addEventListener('change', e => {
      if (!e.target.name?.startsWith('options[')) return;

      const options = [...form.querySelectorAll('[name^="options["]')]
        .filter(el => el.type !== 'radio' || el.checked)
        .map(el => el.value);

      const opts = [...select.options];
      for (const opt of opts) {
        const variantTitle = opt.text.split(' - ')[0];
        if (options.every(o => variantTitle.includes(o))) {
          select.value = opt.value;

          const price = opt.dataset.price;
          if (priceEl && price) {
            priceEl.textContent = formatMoney(parseInt(price, 10));
          }

          const atcBtn = form.querySelector('.product-atc');
          if (atcBtn) {
            const available = !opt.disabled;
            atcBtn.disabled = !available;
            atcBtn.textContent = available ? 'Add to Collection' : 'Sold';
          }
          break;
        }
      }
    });
  });

  /* ── Minimal money formatter ── */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* ─────────────────────────────────────────────
     SCROLL ANIMATIONS — directional fade-in
  ───────────────────────────────────────────── */

  if ('IntersectionObserver' in window) {

    /* Auto-assign data-fade to content elements */
    function assignFadeAttrs() {
      /* Directional pairs */
      document.querySelectorAll('.muse-story__images').forEach(el => {
        if (!el.dataset.fade) el.dataset.fade = 'left';
      });
      document.querySelectorAll('.muse-story__content').forEach(el => {
        if (!el.dataset.fade) el.dataset.fade = 'right';
      });
      document.querySelectorAll('.product-page__images').forEach(el => {
        if (!el.dataset.fade) el.dataset.fade = 'left';
      });
      document.querySelectorAll('.product-page__details').forEach(el => {
        if (!el.dataset.fade) el.dataset.fade = 'right';
      });

      /* Stagger grids */
      document.querySelectorAll(
        '.featured-products__grid, .gallery-grid, .social-grid'
      ).forEach(el => {
        if (!el.dataset.fadeStagger) el.dataset.fadeStagger = '';
      });

      /* Section headers fade up */
      document.querySelectorAll(
        '.featured-products__header, .social-proof__header, .gallery-page__header, ' +
        '.sustain-message__inner, .st-header, .testimonials, ' +
        '.email-capture__inner, .acknowledgements__header'
      ).forEach(el => {
        if (!el.dataset.fade) el.dataset.fade = '';
      });

      /* Donation tiers */
      document.querySelectorAll('.st-tiers').forEach(el => {
        if (!el.dataset.fade) el.dataset.fade = '';
      });
    }

    assignFadeAttrs();

    /* Observer for [data-fade] */
    const fadeIO = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            fadeIO.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.07, rootMargin: '0px 0px -48px 0px' }
    );

    /* Observer for [data-fade-stagger] */
    const staggerIO = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            staggerIO.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -32px 0px' }
    );

    /* Observe everything */
    function observe() {
      document.querySelectorAll('[data-fade]').forEach(el => fadeIO.observe(el));
      document.querySelectorAll('[data-fade-stagger]').forEach(el => staggerIO.observe(el));
    }

    observe();
  }

  /* ─────────────────────────────────────────────
     PARALLAX — hero image on scroll
  ───────────────────────────────────────────── */

  const parallaxImg = document.querySelector('.hero-banner__image');

  if (parallaxImg) {
    let ticking = false;

    function onParallaxScroll() {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          const speed   = 0.28;
          parallaxImg.style.transform = `scale(1.06) translateY(${scrollY * speed}px)`;
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onParallaxScroll, { passive: true });
  }

  /* ─────────────────────────────────────────────
     GOLD NAV PARTICLES — hover effect
  ───────────────────────────────────────────── */

  function spawnNavParticles(linkEl) {
    const rect    = linkEl.getBoundingClientRect();
    const count   = 10;
    const spread  = rect.width;

    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        const p    = document.createElement('div');
        p.className = 'nav-particle';

        /* Random position along the bottom edge of the link text */
        const x = rect.left + Math.random() * spread;
        const y = rect.top + rect.height * 0.6;

        /* Random size: 2–5px */
        const size = 2 + Math.random() * 3;
        p.style.width  = size + 'px';
        p.style.height = size + 'px';

        /* Random horizontal drift */
        const tx = (Math.random() * 24 - 12) + 'px';
        p.style.setProperty('--tx', tx);

        /* Random duration: 0.6–1.1s */
        const dur = (0.6 + Math.random() * 0.5).toFixed(2) + 's';
        p.style.setProperty('--particle-dur', dur);

        /* Position fixed at cursor coords */
        p.style.left = x + 'px';
        p.style.top  = y + 'px';

        document.body.appendChild(p);
        setTimeout(() => { if (p.parentNode) p.parentNode.removeChild(p); }, 1200);
      }, i * 55);
    }
  }

  /* Attach to all nav links — desktop + mobile */
  function initNavParticles() {
    document.querySelectorAll('.site-nav__link, .mobile-nav__link').forEach(link => {
      link.addEventListener('mouseenter', () => spawnNavParticles(link));
    });
  }

  document.addEventListener('DOMContentLoaded', initNavParticles);

  /* ─────────────────────────────────────────────
     GOLD PAINT DRIP — click effect
  ───────────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    /* Skip clicks on interactive elements */
    const tag = e.target.tagName;
    if (['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL'].includes(tag)) return;

    const drip = document.createElement('div');
    drip.className = 'gold-drip';
    drip.style.left = e.clientX + 'px';
    drip.style.top  = e.clientY + 'px';

    const dot  = document.createElement('div');
    dot.className = 'gold-drip__dot';
    const tail = document.createElement('div');
    tail.className = 'gold-drip__tail';

    drip.appendChild(dot);
    drip.appendChild(tail);
    document.body.appendChild(drip);

    /* Clean up after animation */
    setTimeout(() => {
      if (drip.parentNode) drip.parentNode.removeChild(drip);
    }, 1200);
  });

})();
